import React, { useContext, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { ThemeProvider } from 'styled-components';
import nebulaTheme from '@nebula/theme';
import { InfoIcon } from '@nebula/icons/ui';
import { ChangeBillingDateStage, changeBillingDateTransition } from '@ecs-uk/web-common-mobile-view-helpers';
import { publishTryAgainClick } from '../../../helpers/changeBillingDate/analytics';
import { MainContent } from '../change-billing-date-success/index.styles';

import {
  ErrorState,
  ContentBox,
  Heading,
  SubHeading,
  TryAgainButton,
  Container,
  InfoIconContainer,
} from './index.styles';
import { PaymentContext } from '../../../components/PaymentOrchestrator/PaymentContextProvider';
import usePageView from '../../../hooks/usePageView';

const PAGE_NAME = 'ProdMoveChangeBillingDateFailurePage';

const ChangeBillingDateFailure = ({
  heading = 'Unable to change billing date',
  subheading = 'It was an issue at our end. You have not been charged.',
  onActionClick,
  onPageLoad,
}) => {
  const ariaLabel = useMemo(() => `${heading}. ${subheading}`, [heading, subheading]);
  const { isWebview } = useContext(PaymentContext);

  usePageView(PAGE_NAME);

  useEffect(() => {
    onPageLoad?.();

    if (isWebview) {
      changeBillingDateTransition({ stage: ChangeBillingDateStage.FAILURE });
      return undefined;
    }

    // Focus heading for screen readers to announce failure
    const timer = setTimeout(() => {
      document.getElementById('failure-section')?.focus();
    }, 100);

    return () => clearTimeout(timer);
  }, [isWebview]);

  return (
    <ThemeProvider theme={nebulaTheme}>
      <Container isWebview={isWebview}>
        <MainContent>
          <ErrorState>
            <InfoIconContainer>
              <InfoIcon iconSize="large" />
            </InfoIconContainer>
            <ContentBox id="failure-section" tabIndex={-1} as="section" aria-label={ariaLabel}>
              <Heading aria-hidden="true">{heading}</Heading>
              <SubHeading aria-hidden="true">{subheading}</SubHeading>
            </ContentBox>
            <TryAgainButton
              id="try_again"
              type="button"
              label="Try again"
              onClick={() => {
                publishTryAgainClick();
                onActionClick?.();
              }}
            />
          </ErrorState>
        </MainContent>
      </Container>
    </ThemeProvider>
  );
};

ChangeBillingDateFailure.propTypes = {
  heading: PropTypes.string,
  subheading: PropTypes.string,
  onActionClick: PropTypes.func,
  onPageLoad: PropTypes.func,
};

export default ChangeBillingDateFailure;
